/* =============================================================
   LIA Warenkorb (Drawer)
   - Geschenk automatisch hinzufügen/entfernen (Status kommt aus Liquid:
     [data-lia-gift][data-action="add"|"remove"])
   - Fortschrittsbalken vom alten zum neuen Stand animieren
   - „Passend dazu“: Schnell-Hinzufügen (Button oder Farb-Select)
   ============================================================= */
(() => {
  if (window.__liaCart) return;
  window.__liaCart = true;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  let lastProgress = null;
  let busy = false;
  const attempts = new Map();

  const drawerEl = () => document.querySelector('cart-drawer');
  const root = () => document.getElementById('CartDrawer');

  const post = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
    }).then((r) => r.json());

  function render(state) {
    const drawer = drawerEl();
    if (!drawer || !state || !state.sections) return;
    drawer.renderContents(state, false);
    enableTabbing();
  }

  // Im Drawer nur geöffnet fokussierbar; auf /cart ist der Warenkorb immer sichtbar
  const isCartPage = () => document.body.classList.contains('template-cart');
  function enableTabbing() {
    const drawer = drawerEl();
    if (!drawer || typeof setTabindex !== 'function') return;
    if (drawer.isOpen || isCartPage()) setTabindex(drawer.toggleEelements(), '0');
  }

  /* ---------- Balken animieren ---------- */
  function animateBar() {
    const tiers = root()?.querySelector('.lia-tiers');
    if (!tiers) return;
    const next = parseFloat(tiers.dataset.liaProgress) || 0;
    const fill = tiers.querySelector('.lia-tiers__fill');
    if (fill && lastProgress !== null && lastProgress !== next) {
      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${lastProgress})`;
      fill.getBoundingClientRect(); // Reflow
      fill.style.transition = '';
      fill.style.transform = `scaleX(${next})`;
    }
    lastProgress = next;
  }

  /* ---------- Geschenk ---------- */
  function syncGift() {
    const gift = root()?.querySelector('[data-lia-gift]');
    const action = gift?.dataset.action || '';
    if (!action) {
      attempts.clear();
      return;
    }
    if (busy) return;

    const key = `${action}:${gift.dataset.variantId}`;
    const n = attempts.get(key) || 0;
    if (n >= 2) return; // Schleifenschutz (z. B. Variante nicht lieferbar)
    attempts.set(key, n + 1);

    busy = true;
    const addGift = () =>
      post(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        items: [{ id: Number(gift.dataset.variantId), quantity: 1, properties: { _lia_gift: '1' } }],
      });
    const removeGift = () =>
      post(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
        id: gift.dataset.lineKey,
        quantity: 0,
      });

    let req;
    if (action === 'add') req = addGift();
    else if (action === 'remove') req = removeGift();
    else if (action === 'swap') {
      // Farbe getauscht: alte Geschenk-Position raus, neue rein
      req = removeGift().then((state) => {
        if (state.status && state.status >= 400) throw state;
        return addGift();
      });
    } else {
      busy = false;
      return;
    }

    req
      .then((state) => {
        if (state.status && state.status >= 400) throw state;
        if (action === 'add') sessionStorageSafe('lia-gift-unlocked', '1');
        render(state);
      })
      .catch((e) => console.warn('[LIA Warenkorb] Geschenk', e))
      .finally(() => {
        busy = false;
      });
  }

  function sessionStorageSafe(k, v) {
    try {
      if (v === undefined) {
        const val = sessionStorage.getItem(k);
        sessionStorage.removeItem(k);
        return val;
      }
      sessionStorage.setItem(k, v);
    } catch (e) {
      return null;
    }
  }

  function markJustUnlocked() {
    if (sessionStorageSafe('lia-gift-unlocked') !== '1') return;
    root()?.querySelector('.lia-gift.is-unlocked')?.classList.add('lia-gift--just-unlocked');
  }

  /* ---------- Geschenk: Farbe wählen ---------- */
  function chooseGiftVariant(gift, variantId, name, image) {
    if (!gift || !variantId) return;

    // Sofortiges Feedback: Text + Bild tauschen, bevor der Server antwortet
    const value = gift.querySelector('[data-lia-gift-value]');
    if (value && name) value.textContent = name;
    const img = gift.querySelector('.lia-gift__thumb img');
    if (img && image) {
      img.removeAttribute('srcset');
      img.src = image;
    }
    gift.querySelectorAll('[data-lia-gift-pick]').forEach((b) => {
      const on = b.dataset.liaGiftPick === String(variantId);
      b.classList.toggle('is-selected', on);
      if (on) b.setAttribute('aria-pressed', 'true');
      else b.removeAttribute('aria-pressed');
    });
    gift.classList.add('is-switching');

    const save = () =>
      post(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        attributes: { _lia_gift_variant: String(variantId) },
      })
        .then((state) => {
          busy = false;
          attempts.clear();
          render(state); // Liquid setzt ggf. action="swap" → syncGift tauscht die Position
        })
        .catch((e) => {
          busy = false;
          gift.classList.remove('is-switching');
          console.warn('[LIA Warenkorb] Geschenk-Farbe', e);
        });

    if (busy) {
      const wait = setInterval(() => {
        if (!busy) { clearInterval(wait); busy = true; save(); }
      }, 100);
      return;
    }
    busy = true;
    save();
  }

  /* ---------- Farbpunkte: Zeile auf/zu ---------- */
  function closePickers(except) {
    root()?.querySelectorAll('.lia-picker:not([hidden])').forEach((pk) => {
      if (pk === except) return;
      pk.hidden = true;
      root().querySelector(`[data-lia-picker="${pk.id}"]`)?.setAttribute('aria-expanded', 'false');
    });
  }
  function togglePicker(btn) {
    const pk = document.getElementById(btn.dataset.liaPicker);
    if (!pk) return;
    closePickers(pk);
    const open = pk.hidden;
    pk.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    if (open) {
      pk.querySelectorAll('[tabindex="-1"]').forEach((el) => el.setAttribute('tabindex', '0'));
      pk.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }
  const showName = (sw) => {
    const label = sw.closest('.lia-picker')?.querySelector('[data-lia-picker-name]');
    if (label) label.textContent = sw.dataset.name || '';
  };
  document.addEventListener('pointerover', (e) => {
    const sw = e.target.closest?.('.lia-cart .lia-swatch');
    if (sw) showName(sw);
  });
  document.addEventListener('focusin', (e) => {
    const sw = e.target.closest?.('.lia-cart .lia-swatch');
    if (sw) showName(sw);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const pk = e.target.closest?.('.lia-cart .lia-picker');
    if (!pk) return;
    e.stopPropagation();
    pk.hidden = true;
    const btn = root()?.querySelector(`[data-lia-picker="${pk.id}"]`);
    btn?.setAttribute('aria-expanded', 'false');
    btn?.focus();
  }, true);

  /* ---------- Passend dazu ---------- */
  function addUpsell(variantId, trigger) {
    if (!variantId || busy) return;
    busy = true;
    trigger?.classList.add('is-loading');
    post(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      items: [{ id: Number(variantId), quantity: 1 }],
    })
      .then((state) => {
        if (state.status && state.status >= 400) throw state;
        busy = false;
        render(state);
      })
      .catch((e) => {
        console.warn('[LIA Warenkorb] Hinzufügen', e);
        busy = false;
        trigger?.classList.remove('is-loading');
      });
  }

  document.addEventListener('click', (e) => {
    const pickBtn = e.target.closest('.lia-cart [data-lia-picker]');
    if (pickBtn) {
      e.preventDefault();
      togglePicker(pickBtn);
      return;
    }
    const closeBtn = e.target.closest('.lia-cart [data-lia-picker-close]');
    if (closeBtn) {
      e.preventDefault();
      closePickers();
      return;
    }
    const giftPick = e.target.closest('.lia-cart [data-lia-gift-pick]');
    if (giftPick) {
      e.preventDefault();
      const gift = giftPick.closest('[data-lia-gift]');
      gift.querySelector('.lia-picker').hidden = true;
      gift.querySelector('[data-lia-picker]')?.setAttribute('aria-expanded', 'false');
      chooseGiftVariant(gift, giftPick.dataset.liaGiftPick, giftPick.dataset.name, giftPick.dataset.image);
      return;
    }
    const btn = e.target.closest('.lia-cart [data-lia-add]');
    if (!btn) return;
    e.preventDefault();
    addUpsell(btn.dataset.liaAdd, btn);
  });
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('.lia-cart [data-lia-add-select]');
    if (!sel) return;
    e.stopPropagation();
    addUpsell(sel.value, sel.closest('.lia-upsell__add'));
  }, true);

  /* ---------- Beobachten ---------- */
  // Theme setzt .wt-cart--empty nur beim Seitenaufruf → nach Re-Render korrigieren
  function syncEmptyClass() {
    const drawer = drawerEl();
    if (!drawer) return;
    drawer.classList.toggle('wt-cart--empty', !root()?.querySelector('.wt-cart__list'));
  }

  // Aufgeklappte Summe bleibt nach Re-Render offen
  let sumOpen = false;
  document.addEventListener('toggle', (e) => {
    if (e.target.matches?.('[data-lia-sum]')) sumOpen = e.target.open;
  }, true);
  function restoreSum() {
    const sum = root()?.querySelector('[data-lia-sum]');
    if (sum && sumOpen && !sum.open) sum.open = true;
  }

  function onRender() {
    if (isCartPage()) enableTabbing();
    restoreSum();
    syncEmptyClass();
    animateBar();
    markJustUnlocked();
    syncGift();
  }

  function init() {
    const el = root();
    if (!el) return;
    new MutationObserver(() => {
      clearTimeout(init.t);
      init.t = setTimeout(onRender, 30);
    }).observe(el, { childList: true, subtree: true });
    onRender();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();



